import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import axiosRetry from 'axios-retry';
import { defaultApiConfig, type ApiConfig, type ApiError, type ApiErrorResponse } from './types';
import { reachabilityStore } from './reachability';
import { nextServerIndex } from './serverUrls';

/**
 * Token getter function type
 */
type TokenGetter = () => string | null;

/**
 * Token refresh function type
 */
type TokenRefresher = () => Promise<string | null>;

/**
 * Logout callback type
 */
type LogoutCallback = () => void;

/**
 * API Client Factory - Creates configured Axios instances
 */
class ApiClientFactory {
  private static instance: ApiClientFactory;
  private client: AxiosInstance;
  private config: ApiConfig;
  private tokenGetter: TokenGetter = () => null;
  private tokenRefresher: TokenRefresher | null = null;
  private logoutCallback: LogoutCallback | null = null;
  private isRefreshing = false;
  private refreshSubscribers: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = [];
  private customHeaders: Map<string, string> = new Map();
  private isLoggingOut = false;
  private static readonly MAX_REFRESH_RETRIES = 1;

  /** Ordered server base URLs for failover; `activeIndex` is the current one (sticky). */
  private baseURLs: string[];
  private activeIndex = 0;

  private constructor(config: ApiConfig = defaultApiConfig) {
    this.config = config;
    this.baseURLs = config.baseURLs?.length ? [...config.baseURLs] : [config.baseURL];
    this.client = this.createClient();
  }

  /** True for a transport-level failure (no HTTP response) — i.e. server unreachable. */
  private static isNetworkError(error: AxiosError): boolean {
    return !error.response;
  }

  /**
   * Advance to the next configured server and re-point the live instance at it.
   * A no-op when only one server is configured (failover stays dormant). Returns
   * the now-active base URL.
   */
  private rotateToNextServer(): string {
    this.activeIndex = nextServerIndex(this.activeIndex, this.baseURLs.length);
    const url = this.baseURLs[this.activeIndex] ?? this.config.baseURL;
    this.config.baseURL = url;
    this.client.defaults.baseURL = url;
    return url;
  }

  /**
   * Get singleton instance
   */
  public static getInstance(config?: ApiConfig): ApiClientFactory {
    if (!ApiClientFactory.instance) {
      ApiClientFactory.instance = new ApiClientFactory(config);
    }
    return ApiClientFactory.instance;
  }

  /**
   * Create configured Axios instance
   */
  private createClient(): AxiosInstance {
    const instance = axios.create({
      baseURL: this.baseURLs[this.activeIndex] ?? this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
    });

    // Configure retry logic
    axiosRetry(instance, {
      retries: this.config.retry.retries,
      retryDelay: (retryCount) => {
        if (this.config.retry.exponentialBackoff) {
          return Math.pow(2, retryCount) * this.config.retry.retryDelay;
        }
        return this.config.retry.retryDelay;
      },
      retryCondition: (error: AxiosError) => {
        const status = error.response?.status;
        if (!status) return true; // Network error
        return this.config.retry.retryCondition.includes(status);
      },
      onRetry: (retryCount, error, requestConfig) => {
        console.warn(`[API] Retry attempt ${retryCount} for ${error.config?.url}`);
        // A network error means the current server is unreachable: surface the
        // "reconnecting" state and fail over to the next server for this retry.
        if (ApiClientFactory.isNetworkError(error)) {
          reachabilityStore.set('reconnecting');
          const nextUrl = this.rotateToNextServer();
          requestConfig.baseURL = nextUrl;
        }
      },
    });

    // Request interceptor
    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => this.handleRequest(config),
      (error: AxiosError) => Promise.reject(error)
    );

    // Response interceptor — a response (even a 4xx/5xx) proves the server is reachable.
    instance.interceptors.response.use(
      (response: AxiosResponse) => {
        reachabilityStore.set('online');
        return response;
      },
      (error: AxiosError<ApiErrorResponse>) => this.handleResponseError(error)
    );

    return instance;
  }

  /**
   * Handle outgoing request
   */
  private handleRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    // Add auth token
    const token = this.tokenGetter();
    if (token && !config.headers.get('Authorization')) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    // Add custom headers
    this.customHeaders.forEach((value, key) => {
      if (!config.headers.get(key)) {
        config.headers.set(key, value);
      }
    });

    // Add request timestamp for debugging
    if (import.meta.env.DEV) {
      config.headers.set('X-Request-Time', new Date().toISOString());
    }

    return config;
  }

  /**
   * Handle response errors
   */
  private async handleResponseError(error: AxiosError<ApiErrorResponse>): Promise<never> {
    const originalRequest = error.config as InternalAxiosRequestConfig & { retryCount?: number };

    // Reachability: no response after retries were exhausted (across all servers)
    // means the server is unreachable; a response means it is reachable.
    reachabilityStore.set(ApiClientFactory.isNetworkError(error) ? 'offline' : 'online');

    if (error.response?.status === 401 && !this.isLoggingOut) {
      if (this.isRefreshTokenRequest(originalRequest)) {
        this.handleLogout();
        return Promise.reject(this.toApiError(error));
      }

      if (this.isAnonymousAuthRequest(originalRequest)) {
        return Promise.reject(this.toApiError(error));
      }

      const retryCount = originalRequest.retryCount ?? 0;
      if (retryCount < ApiClientFactory.MAX_REFRESH_RETRIES && this.tokenRefresher) {
        return this.handleTokenRefresh(originalRequest) as Promise<never>;
      }
    }

    return Promise.reject(this.toApiError(error));
  }

  private isRefreshTokenRequest(config?: InternalAxiosRequestConfig): boolean {
    return !!config?.url?.includes('/auth/refresh-token');
  }

  private isAnonymousAuthRequest(config?: InternalAxiosRequestConfig): boolean {
    const url = config?.url ?? '';
    // Explicit paths only: the invitee-facing endpoints are anonymous-capable, but the
    // clinic-scoped `/clinic/invitations/...` management endpoints are NOT — a loose
    // `/invitations/` check would swallow them.
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/otp/send') ||
      url.includes('/auth/password-reset') ||
      url.includes('/invitations/preview') ||
      url.includes('/invitations/accept') ||
      url.includes('/invitations/decline')
    );
  }

  private toApiError(error: AxiosError<ApiErrorResponse>): ApiError {
    return normalizeApiError(error);
  }

  /**
   * Handle token refresh
   */
  private async handleTokenRefresh(
    originalRequest: InternalAxiosRequestConfig & { retryCount?: number }
  ): Promise<AxiosResponse> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push({
          resolve: (token: string) => {
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
            resolve(this.client(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest.retryCount = (originalRequest.retryCount ?? 0) + 1;
    this.isRefreshing = true;

    try {
      const newToken = await this.tokenRefresher!();
      if (newToken) {
        this.refreshSubscribers.forEach((subscriber) => subscriber.resolve(newToken));
        this.refreshSubscribers = [];
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        return this.client(originalRequest);
      } else {
        this.rejectAllSubscribers(new Error('Token refresh failed'));
        this.handleLogout();
        throw new Error('Token refresh failed');
      }
    } catch (refreshError) {
      this.rejectAllSubscribers(refreshError);
      this.handleLogout();
      throw refreshError;
    } finally {
      this.isRefreshing = false;
    }
  }

  private rejectAllSubscribers(error: unknown): void {
    this.refreshSubscribers.forEach((subscriber) => subscriber.reject(error));
    this.refreshSubscribers = [];
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    this.rejectAllSubscribers(new Error('Session expired'));
    this.isRefreshing = false;

    if (this.logoutCallback) {
      this.logoutCallback();
    }

    if (typeof window !== 'undefined') {
      if (
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/register') ||
        window.location.pathname.startsWith('/reset-password') ||
        // The public invitation-accept screen owns its own sign-in handover.
        window.location.pathname.startsWith('/invitations/')
      ) {
        this.isLoggingOut = false;
        return;
      }

      setTimeout(() => {
        this.isLoggingOut = false;
        window.location.href = '/login';
      }, 0);
    } else {
      this.isLoggingOut = false;
    }
  }

  // =====================
  // Public Configuration Methods
  // =====================

  /**
   * Set token getter function
   */
  public setTokenGetter(getter: TokenGetter): void {
    this.tokenGetter = getter;
  }

  /**
   * Set token refresh function
   */
  public setTokenRefresher(refresher: TokenRefresher): void {
    this.tokenRefresher = refresher;
  }

  /**
   * Set logout callback
   */
  public setLogoutCallback(callback: LogoutCallback): void {
    this.logoutCallback = callback;
  }

  /**
   * Add a custom header to all requests
   */
  public addHeader(key: string, value: string): void {
    this.customHeaders.set(key, value);
  }

  /**
   * Remove a custom header
   */
  public removeHeader(key: string): void {
    this.customHeaders.delete(key);
  }

  /**
   * Clear all custom headers
   */
  public clearHeaders(): void {
    this.customHeaders.clear();
  }

  /**
   * Update base URL. Resets the failover list to this single URL.
   */
  public setBaseURL(url: string): void {
    this.config.baseURL = url;
    this.baseURLs = [url];
    this.activeIndex = 0;
    this.client.defaults.baseURL = url;
  }

  /**
   * Replace the ordered failover server list at runtime (primary first). The
   * active server resets to the first entry. Empty input is ignored.
   */
  public setServerUrls(urls: string[]): void {
    if (urls.length === 0) return;
    this.baseURLs = [...urls];
    this.activeIndex = 0;
    this.config.baseURL = urls[0];
    this.client.defaults.baseURL = urls[0];
  }

  /** Current active base URL (the server requests are hitting right now). */
  public getActiveBaseURL(): string {
    return this.baseURLs[this.activeIndex] ?? this.config.baseURL;
  }

  /** Ordered list of configured server base URLs (primary first). */
  public getServerUrls(): string[] {
    return [...this.baseURLs];
  }

  /** Pin the active server to a specific configured URL (used after a probe finds a live one). */
  public activateServer(url: string): void {
    const index = this.baseURLs.indexOf(url);
    if (index === -1) return;
    this.activeIndex = index;
    this.config.baseURL = url;
    this.client.defaults.baseURL = url;
  }

  /**
   * Update timeout
   */
  public setTimeout(timeout: number): void {
    this.config.timeout = timeout;
    this.client.defaults.timeout = timeout;
  }

  /**
   * Get the Axios instance
   */
  public getClient(): AxiosInstance {
    return this.client;
  }
}

// Export singleton instance
export const apiClientFactory = ApiClientFactory.getInstance();
export const apiClient = apiClientFactory.getClient();

export function normalizeApiError(error: AxiosError<ApiErrorResponse>): ApiError {
  const data = error.response?.data;
  const status = error.response?.status || 0;
  const fallbackMessage = error.message || 'An unexpected error occurred';

  if (typeof data === 'string' && data.trim()) {
    return {
      message: data,
      code: error.code,
      status,
      timestamp: new Date().toISOString(),
    };
  }

  const responseData = typeof data === 'object' && data !== null ? data : undefined;
  const responseMessage =
    responseData?.message ||
    responseData?.detail ||
    firstValidationError(responseData?.errors) ||
    responseData?.title ||
    responseData?.error;

  return {
    message: responseMessage || fallbackMessage,
    code: responseData?.code || error.code,
    status,
    errors: responseData?.errors,
    timestamp: new Date().toISOString(),
  };
}

function firstValidationError(errors?: Record<string, string[]>): string | undefined {
  if (!errors) return undefined;

  for (const messages of Object.values(errors)) {
    const firstMessage = messages.find((message) => message.trim());
    if (firstMessage) return firstMessage;
  }

  return undefined;
}
